import { PhoneNumberDisplay } from 'app/components/PhoneNumberDisplay';
import { useLocalSelector, useLocalSlice } from 'app/data/local';
import { Box, TextField } from 'app/design';
import { Button } from 'app/design-lib';

import { ButtonDropdown } from 'app/design-lib/components/ButtonDropdown';
import { useUpdateUserPartial } from 'app/hooks/mutations/user';
import { useAccountQuery } from 'app/hooks/queries/account';
import { usePipesQuery } from 'app/sdk';

import { useToggleReducer } from 'app/utilities';

import { Emoji } from 'emoji-picker-react';
import EventEmitter from 'eventemitter3';
import { fromJSON } from 'flatted';
import React, { useEffect, useRef, useState } from 'react';
import InputMask from 'react-input-mask';
import { useDispatch } from 'react-redux';
import { useInterval, useScroll } from 'react-use';
import SimpleBar from 'simplebar-react';

import { store } from 'store';
import { useWebphoneSelector } from '../../../../data/webphone';
import { useLookupContact } from '../../../../data/webphone/webphoneSlice';
import {
  useCallDetailRecordsReportQuery,
  useListCdrInteractionsQuery,
} from '../../../../hooks/queries/callDetailRecord';
import useMaybeContactsQuery from '../../../../hooks/queries/contact/useMaybeContactsQuery';
import { useAuthenticatedUserQuery } from '../../../../hooks/queries/user';
import { useEditContactDialog } from '../../../EditContactDialog';
import { MaybeContactDisplay } from '../../../MaybeContactDisplay';
import { useWebphoneContext } from '../../Store';
import { ContactDisplay } from '../ContactDisplay';
import { Numpad } from '../Numpad';

const eventEmitter = new EventEmitter();

// clean number to match expected sip format
export function formatPhoneNumberUS(phoneNumberString) {
  // strip all non-numbers (parenthesis, dash, etc)
  let cleaned = ('' + phoneNumberString).replace(/\W/g, '');

  if (cleaned) {
    // add leading "+" back if present
    if (phoneNumberString.startsWith('+')) {
      cleaned = '+' + cleaned;
    } else if (phoneNumberString.startsWith('*')) {
      cleaned = '*' + cleaned;
    }

    // console.log('cleaned', cleaned);
    return cleaned;
  }

  window.alert('invalid phone number format');
  throw new Error('INVALID NUMBER FORMAT', phoneNumberString);
}

const DialView = (props, context) => {
  const { dial, show, makeCall } = props;
  const { actions } = useLocalSlice();
  const dispatch = useDispatch();
  const inputRef = useRef();

  const [dialerMode, setDialerMode] = useState('internal'); // conference
  const [additionalInfo, setAdditionalInfo] = useState({});
  const { callingDisabled } = useWebphoneSelector();

  // get last call form auto dial number
  const { data: authenticatedUser } = useAuthenticatedUserQuery();
  const { data: interactionsPage, isFetching } = useListCdrInteractionsQuery(
    0,
    1,
    [['createdAt', 'desc']],
    {
      raw: [
        {
          variables: {
            docFilters: [['owner_ids', 'contains', authenticatedUser?.id]],
          },
        },
      ],
    },
  );

  const { cdrInteractions = [], totalCount = 0 } = interactionsPage ?? {};

  // eslint-disable-next-line no-unused-vars
  const [state, dispatchWebphone] = useWebphoneContext();
  const { calls, c, dialerText, isLineConnected } = state;

  const inputText = dialerText;

  const setInputText = val => {
    // console.log('inputText:', { inputText, val });
    dispatchWebphone({
      type: 'sip:update',
      payload: {
        dialerText: typeof val === 'function' ? val(inputText) : val,
      },
    });
  };
  // const accountDefaultNumber = ''; //
  // const [hasNumpad, setNumpad] = useState(true);
  // const [inputText, setInputText] = useState(''); // defaults are set in each Dialer!

  const [showFeatureCodes, toggleFeatureCodes] = useToggleReducer();

  const handleClickNum = num => {
    // // emit click that child listens for
    // setInputText(`${inputText}${num}`);
    inputRef.current?.focus && inputRef.current.focus();
    eventEmitter.emit('add-num', num);
  };

  // const handleStartCall = (isVideoCall, inputText) => {
  //   if (inputText.trim() === '') {
  //     return;
  //   }
  //   const activeCalls = calls.filter(
  //     (call) => call.isActive() && call.isOnLocalHold() === false
  //   );
  //   activeCalls.forEach((activeCall) => {
  //     activeCall.hold();
  //   });
  //   if (dialerMode === 'conference') {
  //     dialConference(inputText, isVideoCall, additionalInfo);
  //   } else {
  //     dial(inputText, isVideoCall);
  //   }
  // };
  const [contact] = useLookupContact(
    // strip leading 1 (country code) if present
    inputText.length === 11 && inputText.startsWith('1')
      ? inputText.substring(1, inputText.length)
      : inputText,
  );
  const { data: maybeContacts } = useMaybeContactsQuery({});
  const maybeContact = maybeContacts?.find(c => c.number === inputText);

  const {
    Dialog: EditContactDialog,
    DialogProps: EditContactDialogProps,
    toggleOpen: toggleOpenEditContactDialog,
  } = useEditContactDialog();

  const handleStartCall = (isVideoCall, inputText) => {
    if (inputText.trim() === '') {
      return;
    }

    // make sure number is US sip format compatible
    const formattedNumber = formatPhoneNumberUS(inputText);

    // if calling is disabled stop call
    // (currently only from having no input or output device)
    if (callingDisabled) return;

    const activeCalls =
      calls?.filter(
        call => call.isActive() && call.isOnLocalHold() === false,
      ) ?? [];

    // if any active calls, place them on hold
    // (calls returns as 'undefined' sometimes?)
    if (activeCalls.length) {
      activeCalls.forEach(activeCall => {
        activeCall.hold();
      });
    }

    // if (dialerMode === 'conference') {
    //   dialConference(inputText, isVideoCall, additionalInfo);
    // } else {

    dial(formattedNumber, isVideoCall);

    // clear dialer
    setInputText('');
    // }
  };

  const handleCall = () => {
    if (!inputText) {
      // add last dialed number to dialer if present
      const lastDialed = cdrInteractions?.[0]?.doc.remoteUser.number;
      if (lastDialed) setInputText(lastDialed);
      return;
    }

    handleStartCall(false, inputText);
  };

  const handleBackspace = () => {
    // remove last char
    setInputText(prev => prev.slice(0, -1));
    inputRef.current?.focus && inputRef.current.focus();
  };

  const ref = useRef(null);
  const scrollRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(1);
  const [topVal, topValSet] = useState([0, 0]);
  // TODO: only run this when dragging, or when resizing (element, window)?
  useInterval(() => {
    if (ref.current && show) {
      // console.log('ref.current.getBoundingClientRect:', ref.current);
      let v = ref.current.getBoundingClientRect();
      let val = window.innerHeight - v.top - 8;
      if (maxHeight != val) {
        setMaxHeight(val);
        topValSet([ref.current.offsetHeight, ref.current.scrollHeight]);
      }
    }
  }, 16);
  const { y } = useScroll(ref);
  let atEnd = y + topVal[0] >= topVal[1] ? true : false;
  // console.log('Y:', {
  //   scrollY: y,
  //   maxHeight,
  //   topVal,
  //   innerHeight: window.innerHeight,
  //   atEnd,
  // });

  const handleAddContact = () => {
    toggleOpenEditContactDialog();
    dispatch(actions.set_webphone_background(true));
  };

  const handleCloseContact = () => {
    toggleOpenEditContactDialog();
    dispatch(actions.set_webphone_background(false));
  };

  return (
    <Box
      sx={{
        display: show ? 'flex' : 'none',
        height: '100%',
        flexDirection: 'column',
      }}
    >
      <EditContactDialog
        {...EditContactDialogProps}
        onClose={handleCloseContact}
        onCancel={handleCloseContact}
        initialNumber={inputText}
        initialName={maybeContact?.name}
        onComplete={handleCloseContact}
      />
      <Box sx={{ flex: '0 1 auto' }}>
        <Box className={`px-4 py-2`}>
          <DialerAutocompleteInput
            show={show}
            setAdditionalInfo={setAdditionalInfo}
            additionalInfo={additionalInfo}
            dialerMode={dialerMode}
            setDialerMode={setDialerMode}
            inputRef={inputRef}
            setDialText={setInputText}
            dial={props.dial}
            dialConference={props.dialConference}
            inputText={inputText}
            setInputText={setInputText}
            onStartCall={handleCall}
          />
        </Box>
        <Box sx={{ display: 'grid', placeItems: 'center' }}>
          {contact ? (
            <ContactDisplay contact={contact} />
          ) : maybeContact ? (
            <>
              <MaybeContactDisplay maybeContact={maybeContact} />
              <Button
                variant={'ghost'}
                color={'accent'}
                onClick={handleAddContact}
              >
                Add to contacts
              </Button>
            </>
          ) : inputText ? (
            <Button
              variant={'ghost'}
              color={'accent'}
              onClick={handleAddContact}
            >
              Add to contacts
            </Button>
          ) : null}
        </Box>
      </Box>
      {/* <div
        ref={ref}
        className={['overflow-hidden'].join(' ')}
        style={{ maxHeight: maxHeight + 'px' }}
      > */}
      <SimpleBar
        scrollableNodeProps={{ ref: ref }}
        className={[
          'flex flex-col items-center h-full',
          y > 0 ? 'shadow-inner' : '',
        ].join(' ')}
        style={{ maxHeight: maxHeight + 'px' }}
      >
        {/* <div
        ref={ref}
        className={[
          'overflow-y-auto flex flex-col items-center',
          y > 0 ? 'shadow-inner' : '',
        ].join(' ')}
        style={{ maxHeight: maxHeight + 'px' }}
        // sx={{
        //   flex: 1,
        //   overflowY: 'auto',
        //   // background: '#285E70',
        //   alignItems: 'center',
        //   display: 'flex',
        //   flexDirection: 'column',
        // }}
      > */}
        <Numpad
          show={show}
          onClick={handleClickNum}
          onCall={handleCall}
          disableCall={
            (!inputText && !cdrInteractions.length) ||
            !makeCall ||
            !isLineConnected ||
            !isLineConnected()
          }
          disableBackspace={!inputText}
          onDelete={handleBackspace}
          showFeatureCodes={showFeatureCodes}
          // toggleFeatureCodes={toggleFeatureCodes}
        />
        {/* {showFeatureCodes ? (
          <div className={`h-full`} sx={{ overflowY: 'auto', color: '#fff' }}>
            <PerfectScrollbar>
              <FeatureCodeContainer />
            </PerfectScrollbar>
          </div>
        ) : null}*/}
        {process.env.NODE_ENV === 'development' ? (
          <button
            onClick={() => {
              // console.log('CALLS:', calls);
              const id = Date.now();
              dispatchWebphone({
                type: 'sip:update',
                payload: {
                  calls: [
                    ...(calls ?? []),
                    {
                      ...TMP_CALL,
                      getId: () => id,
                      getDisposition: () => 'test',
                      isActive: () => true,
                      isRinging: () => true,
                      isMediaActive: () => true,
                      getAdditionalInfo: () => ({}),
                      isAudioOnMute: () => false,
                      isOnLocalHold: () => false,
                      sendDTMF: () => console.log('mock senddtmf'),
                      startTime: Date.now(),
                    },
                  ],
                },
              });
            }}
          >
            Test Call
          </button>
        ) : null}
        <div
          className={[
            'fixed bottom-0 left-0 w-full h-[8px] bg-gradient-to-t from-neutral-300/50',
            atEnd ? 'hidden' : '',
          ].join(' ')}
        ></div>
      </SimpleBar>
      {/* </div> */}
    </Box>
  );

  // return (
  //   <div
  //     style={{
  //       height: '100%',
  //       overflowY: 'scroll',
  //       display: show ? 'block' : 'none',
  //     }}
  //   >
  //     <div
  //       style={{
  //         marginBottom: 15,
  //         borderBottom: '1px solid #CFD8DC',
  //       }}
  //     >
  //       <DialerAutocompleteInput
  //         show={show}
  //         setAdditionalInfo={setAdditionalInfo}
  //         additionalInfo={additionalInfo}
  //         dialerMode={dialerMode}
  //         setDialerMode={setDialerMode}
  //         inputRef={inputRef}
  //         setDialText={setInputText}
  //         dial={props.dial}
  //         dialConference={props.dialConference}
  //       />
  //     </div>
  //     {/* <div
  //       style={{
  //         marginBottom: 15,
  //         borderBottom: '1px solid #CFD8DC',
  //       }}
  //     >
  //       <DialerInputs
  //         setAdditionalInfo={setAdditionalInfo}
  //         additionalInfo={additionalInfo}
  //         dialerMode={dialerMode}
  //         setDialerMode={setDialerMode}
  //         inputRef={inputRef}
  //         setDialText={setInputText}
  //       />
  //     </div> */}
  //     <div>
  //       <Numpad onClick={handleClickNum} />
  //     </div>
  //     {/* <div>
  //       <Grid container justify="center">
  //         <Grid
  //           item
  //           md={7}
  //           onClick={() => {
  //             handleStartCall(false);
  //           }}
  //           // className={classes.audioCallButton}
  //         >
  //           <Button variant="contained" color="success" fullWidth>
  //             <CallIcon />
  //           </Button>
  //         </Grid>
  //         <Grid
  //           item
  //           md={5}
  //           onClick={() => {
  //             handleStartCall(true);
  //           }}
  //           // className={classes.videoCallButton}
  //         >
  //           <Button variant="info" color="success" fullWidth>
  //             <VideocamIcon />
  //           </Button>
  //         </Grid>
  //       </Grid>
  //     </div> */}
  //   </div>
  // );
};

const DialerAutocompleteInput = props => {
  const { show, inputRef, setDialText, inputText, setInputText, onStartCall } =
    props;
  const { data: user, refetch } = useAuthenticatedUserQuery();

  const { data: account } = useAccountQuery({});

  const updateUser = useUpdateUserPartial();
  const pipesQuery = usePipesQuery({
    filters: {
      users: {
        path: [store.getState().auth.owner_id, 'enabled'],
        equals: true,
      },
    },
  });
  const pipes = pipesQuery.data?.pipes ?? [];

  const handleChangeOutboundCallerId = async (type, value) => {
    // console.log('TYPE,VALUE:', { type, value });
    const resp = await updateUser.mutateAsync({
      id: user.id,
      caller_id: {
        ...user.doc.caller_id,
        [type]: {
          ...(user.doc[type] || {}),
          number: value,
        },
      },
    });
    refetch();
  };

  // get number history
  const { data, isLoading } = useCallDetailRecordsReportQuery({
    fnName: 'usersRecentRemoteNumbers',
    fnArgs: { userId: user?.id },
  });
  const { result: numberHistory = [] } = data ?? {};

  const [historyIndex, setHistoryIndex] = useState(-1);
  const [state] = useWebphoneContext();
  const { isRegistered, calls, dialerFrom } = state;

  useEffect(() => {
    if (dialerFrom)
      handleChangeOutboundCallerId(
        dialerFrom.length > 6 ? 'external' : 'internal',
        dialerFrom,
      );
  }, [dialerFrom]);

  // const [inputValue, setInputValue] = useState(null); // not used!

  // const handleChange = newValue => {
  //   // gets newValue as a string??
  //   // - or as a specific type to display?
  //   console.log('handleChange DialerAutocompleteInput:', newValue);
  //   // debugger;
  //   setInputText(newValue?.outputValue ?? '');
  //   if (newValue?.call) {
  //     onStartCall(false, newValue?.outputValue);
  //   }
  // };
  // const handleInputChange = newValue => {
  //   console.log('handleInputChange:', newValue);
  //   setInputText(newValue);
  // };
  // const handleBackspace = () => {
  //   // TODO: clear/reset mode if pressing backspace repeatedly with none remaining
  //   setInputText(inputText.slice(0, inputText.length - 1));
  // };
  // TODO: handle Clear (clear/reset mode)

  const addNum = React.useCallback(
    num => {
      // emit click that child listens for
      // setInputText(inputText => `${inputText}${num}`);
      setInputText(`${inputText}${num}`);
      inputRef.current?.numberInputRef?.focus &&
        inputRef.current.numberInputRef.focus();
    },
    [inputText],
  );

  // // focus
  // useEffect(() => {
  //   if (showInput && inputRef.current?.numberInputRef?.focus) {
  //     inputRef.current.numberInputRef.focus();
  //   }
  // }, [showInput]);

  useEffect(() => {
    eventEmitter.on('add-num', addNum);
    return () => {
      eventEmitter.removeListener('add-num', addNum);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addNum]);

  // // update parent when this one changes
  // useEffect(() => {
  //   setDialText(inputText);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [inputText]);

  const { webphone_visible } = useLocalSelector();

  useEffect(() => {
    // go to the END of the input!
    if (show && webphone_visible && inputRef.current) {
      const end = inputRef.current.value.length;
      inputRef.current.setSelectionRange(end, end);
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, webphone_visible]);

  // const classes = useStyles();

  // can be set on Account/User/Device
  // - we are expecting ONLY account/user (skipping Device, for now)
  //   - TODO: disallow if NOT set on user object? if not on Line? (where to disable outgoing? in outbound webhook?)
  const numberInternal =
    user?.doc?.caller_id?.internal?.number ?? user?.doc?.presence_id;
  const numberExternal =
    user?.doc?.caller_id?.external?.number ??
    account?.doc?.caller_id?.external?.number;

  const externalPipes = pipes.filter(p => p.type === 'external');
  const internalPipes = pipes.filter(
    p => p.type === 'personal' || p.type === 'shared',
  );
  // console.log('pipes:', pipes);

  const isAnyInput = inputText.length ? true : false;
  const isExternalInput = inputText.length > 4 ? true : false;
  const isMultipleResults = isExternalInput
    ? externalPipes.length > 1
    : internalPipes.length > 1;

  const externalPipe = externalPipes.find(pipe => pipe.key === numberExternal);

  return (
    <>
      <div
        className={[
          'flex items-center justify-center space-x-4 pb-2 pt-2 text-sm transition duration-500',
          isAnyInput ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      >
        {!isAnyInput ? null : (
          <ButtonDropdown
            // as={React.Fragment}
            variant={'ghost'}
            disabled={!isMultipleResults}
            hideArrow={!isMultipleResults}
            menuItems={
              isExternalInput
                ? externalPipes.map(pipe => ({
                    label: (
                      <div className={'flex space-x-1 p-1 items-center'}>
                        {pipe.metadata.name ? (
                          <Emoji
                            unified={pipe.metadata?.emoji || '1f937'}
                            size={16}
                          />
                        ) : null}
                        {pipe.metadata.name ? (
                          <span>{pipe.metadata.name}</span>
                        ) : null}
                        <span className={'text-neutral-60'}>
                          <PhoneNumberDisplay hideFlag ptn={pipe.key} />
                        </span>
                      </div>
                      // <div className={'flex items-center space-x-2 text-left'}>
                      //   <div
                      //     className={
                      //       'btn-accent-outline text-2xl pointer-events-none rounded-full p-2 flex items-center justify-center'
                      //     }
                      //   >
                      //     <Emoji
                      //       unified={pipe.metadata.emoji || '1f937'}
                      //       size={25}
                      //     />
                      //   </div>
                      //   <div>
                      //     <div className="font-bold text-base">
                      //       {pipe.metadata.name?.length
                      //         ? pipe.metadata.name
                      //         : 'Unnamed'}
                      //     </div>
                      //     <div className="text-md text-neutral-500">
                      //       <PhoneNumberDisplay ptn={pipe.key} width="auto" />
                      //     </div>
                      //   </div>
                      // </div>
                      // <div
                      //   className={
                      //     pipe.key === numberExternal ? 'font-bold' : ''
                      //   }
                      // >
                      //   {pipe.key === numberExternal ? (
                      //     <span className="mr-1">&gt;</span>
                      //   ) : null}
                      //   <PhoneNumberDisplay ptn={pipe.key} />
                      // </div>
                    ),
                    onClick: () => {
                      handleChangeOutboundCallerId('external', pipe.key);
                    },
                  }))
                : internalPipes.map(pipe => ({
                    label: (
                      <div
                        className={
                          pipe.key === numberInternal ? 'font-bold' : ''
                        }
                      >
                        {pipe.key === numberInternal ? (
                          <span className="mr-1">&gt;</span>
                        ) : null}
                        {pipe.key}
                      </div>
                    ),
                    onClick: () => {
                      handleChangeOutboundCallerId('internal', pipe.key);
                    },
                  }))
            }
          >
            <div
              className={
                'flex items-center ' +
                (isMultipleResults ? 'cursor-pointer' : '')
              }
            >
              <span className={'mr-1 font-normal text-neutral-400'}>
                Call from:
              </span>
              <div className={' text-neutral-600'}>
                {isExternalInput ? (
                  externalPipe ? (
                    <div className={'flex space-x-1 p-1 items-center'}>
                      {externalPipe.metadata.name ? (
                        <Emoji
                          unified={externalPipe.metadata?.emoji || '1f937'}
                          size={16}
                        />
                      ) : null}
                      {externalPipe.metadata.name ? (
                        <span>{externalPipe.metadata.name}</span>
                      ) : null}
                      <span className={'text-neutral-60'}>
                        <PhoneNumberDisplay hideFlag ptn={externalPipe.key} />
                      </span>
                    </div>
                  ) : (
                    <PhoneNumberDisplay ptn={numberExternal} width="auto" />
                  )
                ) : (
                  <PhoneNumberDisplay ptn={numberInternal} width="auto" />
                )}
              </div>
              {/*
              {isMultipleResults ? (
                <span className={'font-semibold text-neutral-600'}>
                  <NavArrowDownIcon
                    fr={undefined}
                    height={14}
                    width={14}
                    className="inline-block"
                  />
                </span>
              ) : null}
*/}
            </div>
          </ButtonDropdown>
        )}
        <div className={''}>
          {inputText.length ? (
            <>
              <span className={'mr-1 text-neutral-400'}>Type:</span>
              <span className={'font-medium text-neutral-600'}>
                {inputText.length > 4 ? 'External' : 'Internal'}
              </span>
            </>
          ) : (
            <>&nbsp;</>
          )}
        </div>
      </div>
      <div
        className={`pt-2`}
        style={{
          display: 'flex',
          flexWrap: 'no-wrap',
          alignItems: 'center', // cant use 'center' if we want full-height buttons
          // padding: '0 12px',
        }}
      >
        {/* <div style={{ width: '28px', textAlign: 'center' }}>
          {typeof isRegistered === 'function' && isRegistered() ? (
            <Tooltip
              title={<>Registered and ready for calls</>}
              type="light"
              arrow
            >
              <span>
                <RegisteredIcon
                  style={{ color: 'green', verticalAlign: 'middle' }}
                />
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={<>Not yet ready for calls</>} type="light" arrow>
              <span>
                <UnRegisteredIcon
                  style={{ color: 'red', verticalAlign: 'middle' }}
                />
              </span>
            </Tooltip>
          )}
        </div> */}
        <div style={{ flex: 1, padding: '6px 0 7px 4px' }}>
          <div style={{ margin2: '8px 0 4px' }}>
            <InputMask
              mask={isExternalInput ? '+1 (999) 999-9999' : ''}
              // value={value}
              // onChange={onChange}
              value={inputText}
              onChange={e => {
                // setDialText(e.target.value)
                let unmasked = e.target.value.replace(/[^\d]/g, '');
                setDialText(unmasked);
                // console.log('change mask:', e?.target?.value, e);
              }}
              // color={conflict ? 'warning' : undefined}
              // error={!!formErrors.phones?.[index]?.mac_address}
              // helperText={
              //   formErrors.phones?.[index]?.mac_address?.message ??
              //   (conflict ? 'MAC address is used in another field' : undefined)
              // }
            >
              <TextField
                inputRef={inputRef}
                autoComplete={'off'}
                placeholder="(000) 000-0000"
                autoFocus
                fullWidth
                size="small"
                margin="none"
                inputProps={{
                  className:
                    'text-center font-bold focus:outline-none focus:ring-0 focus:border-0',
                }}
                sx={{
                  '.MuiInputBase-root': {
                    background: 'none',
                    p: '0px !important',
                  },
                  // background: 'none',
                  input: {
                    p: 0,
                    fontSize: '24px !important',
                    // textAlign: 'right',
                    // background: 'none',
                  },
                  fieldset: {
                    fontSize: 18,
                    border: '0px',
                    // background: 'none',
                  },
                }}
              />
            </InputMask>

            {/* <TextField
              inputRef={inputRef}
              placeholder="(000) 000-0000"
              // label=""
              value={inputText}
              autocomplete={'off'}
              onChange={e => setDialText(e.target.value)}
              inputProps={{
                className:
                  'text-center focus:outline-none focus:ring-0 focus:border-0',

                onKeyDown: e => {
                  const nextIndex = historyIndex + 1;
                  switch (e.key) {
                    case 'Enter':
                      e.preventDefault();
                      // if number in dialer
                      if (inputText) {
                        // start call
                        onStartCall();
                      } else {
                        if (!numberHistory.length) return;
                        // dialer empty, add recent call history
                        setInputText(numberHistory[0]);
                        setHistoryIndex(nextIndex);
                      }
                      break;
                    case 'ArrowUp':
                      e.preventDefault();
                      if (numberHistory.length) {
                        if (nextIndex < numberHistory.length) {
                          setHistoryIndex(nextIndex);
                          setInputText(numberHistory[nextIndex]);
                        } else {
                          setHistoryIndex(-1);
                          setInputText('');
                        }
                      }
                      break;
                    case 'ArrowDown':
                      e.preventDefault();
                      const previousIndex = historyIndex - 1;

                      if (numberHistory.length) {
                        // if start, go to end of list
                        if (historyIndex === -1) {
                          const lastIndex = numberHistory.length - 1;
                          setInputText(numberHistory[numberHistory.length - 1]);
                          setHistoryIndex(lastIndex);
                        } else if (
                          previousIndex < numberHistory.length &&
                          previousIndex >= 0
                        ) {
                          setHistoryIndex(previousIndex);
                          setInputText(numberHistory[previousIndex]);
                          // start of list, clear input
                        } else {
                          setHistoryIndex(-1);
                          setInputText('');
                        }
                      }
                      break;
                    default:
                  }
                },
              }}
              autoFocus
              fullWidth
              size="small"
              margin="none"
              sx={{
                '.MuiInputBase-root': {
                  background: 'none',
                  p: '0px !important',
                },
                // background: 'none',
                input: {
                  p: 0,
                  fontSize: '24px !important',
                  // textAlign: 'right',
                  // background: 'none',
                },
                fieldset: {
                  fontSize: 18,
                  border: '0px',
                  // background: 'none',
                },
              }}
            /> */}
            {/* <AutocompleteDialer
            show={show}
            inputRef={inputRef}
            placeholder="Dial or enter a number"
            label=""
            inputValue={inputText}
            onInputChange={handleInputChange}
            // value={inputValue} // this is NOT here! we ONLY use the inputValue (inputText) from above!
            onChange={handleChange}
            textfieldProps={{ autoFocus: true }}
          /> */}
          </div>
        </div>
        {/* <div style={{ padding: '0 4px' }}>
        <IconButton onClick={handleBackspace} size="small">
          <BackspaceIcon />
        </IconButton>
      </div> */}
        {/* <div style={{ padding: '0 4px' }}>
        <Button
          color="success"
          variant="contained"
          size="large"
          onClick={() => {
            handleStartCall(false, inputText);
          }}
        >
          <CallIcon />
        </Button>
      </div>
      <div style={{ padding: '0 4px' }}>
        <Button
          color="success"
          variant="outlined"
          size="large"
          onClick={() => {
            handleStartCall(true, inputText);
          }}
        >
          <VideocamIcon />
        </Button>
      </div> */}
      </div>
      {/* <Box
        sx={{
          width: '100%',
          visibility: isLoading ? 'visible' : 'hidden',
          textAlign: 'right',
        }}
      >
        <Typography
          variant={'caption'}
          sx={{ fontStyle: 'italic', color: 'text.secondary' }}
        >
          loading recent calls...
        </Typography>
      </Box> */}
    </>
  );
};

// const DialerInputs = props => {
//   const {
//     inputRef,
//     dialerMode,
//     setDialerMode,
//     additionalInfo,
//     setAdditionalInfo,
//     setDialText,
//   } = props;

//   const popupState = usePopupState({
//     variant: 'popover',
//     popupId: `dialerinputs`,
//   });

//   const handleChangeDialerMode = setTo => () => {
//     setDialerMode(setTo);
//     popupState.close();
//   };

//   let DialerComponent, IconComponent;
//   const MODES = [
//     {
//       id: 'address',
//       name: 'Address Book',
//       icon: PersonIcon,
//     },
//     {
//       id: 'internal',
//       name: 'Internal (Extension)',
//       icon: DialpadIcon,
//     },
//     {
//       id: 'external',
//       name: 'External',
//       icon: DialpadIcon,
//     },
//     {
//       id: 'conference',
//       name: 'Conference',
//       icon: VideoCallIcon,
//     },
//   ];
//   const currentMode = MODES.find(m => m.id === dialerMode);
//   IconComponent = currentMode?.icon;

//   return (
//     <div
//       style={{
//         display: 'flex',
//         flexWrap: 'no-wrap',
//         alignItems: 'center',
//         // padding: '0 12px',
//       }}
//     >
//       <div style={{ padding: '0 4px' }}>
//         <div {...bindTrigger(popupState)}>
//           {/* <Tooltip
//             title={
//               <>
//                 Change Type of Call
//                 <br />
//                 - Internal
//                 <br />
//                 - External
//                 <br />- Conference
//               </>
//             }
//             type="light"
//             arrow
//           > */}
//           <span style={{ display: 'inline-block' }}>
//             <Button size="xsmall" style={{ padding: '8px 0' }}>
//               <IconComponent fontSize="small" />
//               <ArrowDropDownIcon fontSize="small" />
//             </Button>
//           </span>
//           {/* </Tooltip> */}
//         </div>
//         <Menu {...bindMenu(popupState)}>
//           {MODES.map(mode => {
//             const IconComponent = mode.icon;
//             return (
//               <MenuItem key={mode.id} onClick={handleChangeDialerMode(mode.id)}>
//                 <ListItemIcon style={{ paddingRight: 4 }}>
//                   {IconComponent && <IconComponent font="small" />}
//                 </ListItemIcon>
//                 <ListItemText primary={mode.name} />
//               </MenuItem>
//             );
//           })}
//         </Menu>
//       </div>
//       <AddressBookInput
//         showInput={dialerMode === 'address'}
//         inputRef={inputRef}
//         setDialText={setDialText}
//       />
//       <ExternalInput
//         showInput={dialerMode === 'external'}
//         inputRef={inputRef}
//         setDialText={setDialText}
//       />
//       <InternalInput
//         showInput={dialerMode === 'internal'}
//         inputRef={inputRef}
//         setDialText={setDialText}
//       />
//       <ConferenceInput
//         showInput={dialerMode === 'conference'}
//         inputRef={inputRef}
//         setDialText={setDialText}
//         additionalInfo={additionalInfo}
//         setAdditionalInfo={setAdditionalInfo}
//       />
//       {/* <DialerComponent inputRef={inputRef} /> */}
//     </div>
//   );
// };

// const AddressBookInput = props => {
//   const { inputRef, showInput, setDialText } = props;

//   const [inputText, setInputText] = useState('');
//   const [showAddressBookSettings, setShowAddressBookSettings] = useState(null);
//   const setShowAddressBookSettingsWrap = setTo => () =>
//     setShowAddressBookSettings(setTo);

//   const handleChange = newValue => {
//     setInputText(newValue?.user?.doc?.presence_id ?? '');
//   };
//   const handleInputChange = newValue => {
//     setInputText(newValue);
//   };

//   const handleBackspace = () => {
//     setInputText(inputText.slice(0, inputText.length - 1));
//   };

//   const addNum = num => {
//     // emit click that child listens for
//     setInputText(inputText => `${inputText}${num}`);
//     // console.log('inputRef:', inputRef?.current);
//     inputRef.current?.focus && inputRef.current.focus();
//   };

//   useEffect(() => {
//     if (showInput) {
//       eventEmitter.on('add-num', addNum);
//       return () => {
//         eventEmitter.removeListener('add-num', addNum);
//       };
//     }
//   }, [showInput]);

//   // update parent when this one changes
//   useEffect(() => {
//     if (showInput) {
//       setDialText(inputText);
//     }
//   }, [inputText]);

//   // focus
//   useEffect(() => {
//     if (showInput && inputRef.current?.focus) {
//       inputRef.current.focus();
//     }
//   }, [showInput]);

//   if (!showInput) {
//     return null;
//   }

//   return (
//     <>
//       {showAddressBookSettings && (
//         <AddressBookSettings onClose={setShowAddressBookSettingsWrap(null)} />
//       )}
//       <div style={{ flex: 1, padding: '4px 0' }}>
//         <div style={{ margin2: '7px 0 0px' }}>
//           <AutocompleteUser
//             inputRef={inputRef}
//             placeholder="Search your linked address books"
//             label=""
//             inputValue={inputText}
//             onInputChange={handleInputChange}
//             onChange={handleChange}
//             autocompleteProps={{ freeSolo: true }}
//             inputProps={{ autoFocus: true }}
//           />
//         </div>
//       </div>
//       <div style={{ padding: '0 4px' }}>
//         <IconButton
//           onClick={handleBackspace}
//           size="small"
//           disabled={!inputText.length}
//         >
//           <BackspaceIcon />
//         </IconButton>
//       </div>
//       <div style={{ padding: '0 4px' }}>
//         <IconButton onClick={setShowAddressBookSettingsWrap(true)} size="small">
//           <SettingsIcon />
//         </IconButton>
//       </div>
//     </>
//   );
// };

// const AddressBookSettings = props => {
//   const { onClose } = props;

//   return (
//     <Dialog fullWidth maxWidth="sm" open onClose={onClose}>
//       <DialogTitle disableTypography>
//         <Grid container justify="space-between">
//           <Grid item>
//             <Typography variant="h6">Sync Contacts in Address Books</Typography>
//           </Grid>
//           {/* <Grid item>
//             <Button color="success">Advanced Setup</Button>
//           </Grid> */}
//         </Grid>
//       </DialogTitle>
//       <Divider />
//       <DialogContent style={{ padding: '8px 12px' }}>
//         <div style={{ width: '100%' }}>
//           <div
//             style={{ textAlign: 'center', maxWidth: '300px', margin: '0 auto' }}
//           >
//             <p>
//               Choose from the following services to link your contacts and have
//               them easily available for dialing
//             </p>
//           </div>
//           <div style={{ textAlign: 'center' }}>
//             <Button variant="outlined" onClick={onClose}>
//               Google Contacts
//             </Button>
//             &nbsp;&nbsp;&nbsp;
//             <Button variant="outlined" onClick={onClose}>
//               Microsoft Outlook Contacts
//             </Button>
//             &nbsp;&nbsp;&nbsp;
//             <Button variant="outlined" onClick={onClose}>
//               Salesforce Contacts
//             </Button>
//           </div>
//         </div>
//       </DialogContent>
//       <Divider />
//       <DialogActions style={{ padding: '8px 24px' }}>
//         <div style={{ width: '100%' }}>
//           <Grid container justify="space-between" alignItems="center">
//             <Grid item></Grid>
//             <Grid item>
//               <Button variant="outlined" onClick={onClose}>
//                 Done
//               </Button>
//             </Grid>
//           </Grid>
//         </div>
//       </DialogActions>
//     </Dialog>
//   );
// };

// const ExternalInput = props => {
//   const { inputRef, showInput, setDialText } = props;

//   const [inputText, setInputText] = useState('1');

//   const handleBackspace = () => {
//     setInputText(inputText.slice(0, inputText.length - 1));
//   };

//   const addNum = num => {
//     // emit click that child listens for
//     setInputText(inputText => `${inputText}${num}`);
//     inputRef.current?.numberInputRef?.focus &&
//       inputRef.current.numberInputRef.focus();
//   };

//   useEffect(() => {
//     if (showInput) {
//       eventEmitter.on('add-num', addNum);
//       return () => {
//         eventEmitter.removeListener('add-num', addNum);
//       };
//     }
//   }, [showInput]);

//   // update parent when this one changes
//   useEffect(() => {
//     if (showInput) {
//       setDialText(inputText);
//     }
//   }, [inputText]);

//   // focus
//   useEffect(() => {
//     if (showInput && inputRef.current?.numberInputRef?.focus) {
//       inputRef.current.numberInputRef.focus();
//     }
//   }, [showInput]);

//   if (!showInput) {
//     return null;
//   }

//   return (
//     <>
//       <div style={{ flex: 1, padding: '6px 0 7px' }}>
//         <div style={{ margin2: '8px 0 4px' }}>
//           <PhoneInput
//             ref={inputRef}
//             country="us"
//             preferredCountries={['us', 'ca']}
//             value={inputText}
//             onChange={newval => setInputText(newval)}
//             // onFocus={props.onFocus}
//             // onBlur={props.onBlur}
//             inputProps={{ autoFocus: true }}
//             inputStyle={{ width: '100%' }}
//           />
//           {
//             // Expected phone input
//             // something else?? ie w/ text?
//           }
//         </div>
//       </div>
//       <div style={{ padding: '0 4px' }}>
//         <IconButton onClick={handleBackspace} size="small">
//           <BackspaceIcon />
//         </IconButton>
//       </div>
//     </>
//   );
// };

// const InternalInput = props => {
//   const { inputRef, showInput, setDialText } = props;

//   const [inputText, setInputText] = useState('');

//   const handleChange = newValue => {
//     setInputText(newValue?.user?.doc?.presence_id ?? '');
//   };
//   const handleInputChange = newValue => {
//     setInputText(newValue);
//   };

//   const handleBackspace = () => {
//     setInputText(inputText.slice(0, inputText.length - 1));
//   };

//   const addNum = num => {
//     // emit click that child listens for
//     setInputText(inputText => `${inputText}${num}`);
//     // console.log('inputRef:', inputRef?.current);
//     inputRef.current?.focus && inputRef.current.focus();
//   };

//   useEffect(() => {
//     if (showInput) {
//       eventEmitter.on('add-num', addNum);
//       return () => {
//         eventEmitter.removeListener('add-num', addNum);
//       };
//     }
//   }, [showInput]);

//   // update parent when this one changes
//   useEffect(() => {
//     if (showInput) {
//       setDialText(inputText);
//     }
//   }, [inputText]);

//   // focus
//   useEffect(() => {
//     if (showInput && inputRef?.current?.focus) {
//       inputRef.current.focus();
//     }
//   }, [showInput]);

//   if (!showInput) {
//     return null;
//   }

//   return (
//     <>
//       <div style={{ flex: 1, padding: '4px 0' }}>
//         <div style={{ margin2: '7px 0 0px' }}>
//           <AutocompleteUser
//             inputRef={inputRef}
//             placeholder="Type Extension or Search Users"
//             label=""
//             inputValue={inputText}
//             onInputChange={handleInputChange}
//             onChange={handleChange}
//             autocompleteProps={{ freeSolo: true }}
//             textfieldProps={{ autoFocus: true }}
//           />
//         </div>
//       </div>
//       <div style={{ padding: '0 4px' }}>
//         <IconButton
//           onClick={handleBackspace}
//           size="small"
//           disabled={!inputText.length}
//         >
//           <BackspaceIcon />
//         </IconButton>
//       </div>
//     </>
//   );
// };

// const ConferenceInput = props => {
//   const {
//     inputRef,
//     showInput,
//     setDialText,
//     additionalInfo,
//     setAdditionalInfo,
//   } = props;

//   const { defaultAccount } = useSetupHook();

//   const [inputText, setInputText] = useState('');

//   const handleChange = newValue => {
//     // NOTE that if we change from a "+" to a "#" that the URI-encoding breaks!!
//     // - need to pass an actual JsSIP.URI with the values pre-built (or do NOT sanitize the INVITE to %23 for the #)
//     if (!newValue?.conference) {
//       return;
//     }

//     console.log('conference:', newValue.conference.doc);
//     // let conferenceString = `conference#${newValue.conference.doc.id}@${defaultAccount.realm}`;
//     let conferenceString = `conference+${newValue.conference.doc.id}`;
//     console.log('conference string:', conferenceString);
//     // setInputText(newValue.conference.doc.conference_numbers[0] ?? '');
//     conferenceString = '+18152696442';

//     // new JsSIP.URI('sip', 'alice', 'atlanta.com', 5060, parameters, headers)

//     setInputText(conferenceString);
//     setAdditionalInfo({
//       type: 'conference',
//       conference: newValue.conference.doc,
//       number: newValue.conference.doc.conference_numbers[0],
//     });
//   };
//   useEffect(() => {
//     if (!showInput) {
//       setAdditionalInfo(null);
//     }
//   }, [showInput]);

//   const handleInputChange = newValue => {
//     setInputText(newValue);
//   };

//   const handleBackspace = () => {
//     setInputText(inputText.slice(0, inputText.length - 1));
//   };

//   const addNum = num => {
//     // emit click that child listens for
//     setInputText(inputText => `${inputText}${num}`);
//     inputRef.current?.focus && inputRef.current.focus();
//   };

//   useEffect(() => {
//     if (showInput) {
//       eventEmitter.on('add-num', addNum);
//       return () => {
//         eventEmitter.removeListener('add-num', addNum);
//       };
//     }
//   }, [showInput]);

//   // update parent when this one changes
//   useEffect(() => {
//     if (showInput) {
//       setDialText(inputText);
//     }
//   }, [inputText]);

//   // focus
//   useEffect(() => {
//     if (showInput && inputRef?.current?.focus) {
//       inputRef.current.focus();
//     }
//   }, [showInput]);

//   if (!showInput) {
//     return null;
//   }

//   return (
//     <>
//       <div style={{ flex: 1, padding: '4px 0' }}>
//         <div style={{ margin2: '7px 0 0px' }}>
//           <AutocompleteConference
//             inputRef={inputRef}
//             placeholder="Search Conferences"
//             label=""
//             inputValue={inputText}
//             onInputChange={handleInputChange}
//             // value={selectedConference}
//             onChange={handleChange}
//             autocompleteProps={{ freeSolo: true }}
//             inputProps={{ autoFocus: true }}
//           />
//         </div>
//       </div>
//       <div style={{ padding: '0 4px' }}>
//         <IconButton
//           onClick={handleBackspace}
//           size="small"
//           disabled={!inputText.length}
//         >
//           <BackspaceIcon />
//         </IconButton>
//       </div>
//     </>
//   );
// };

export default DialView;

const TMP_CALL = fromJSON([
  {
    _debug: true,
    _rtcSession: '1',
    _callConfig: '2',
    _rtcConfig: '3',
    _dtmfOptions: '4',
    _mediaEngine: '5',
    _eventEmitter: '6',
    remoteIdentity: null,
    remoteName: '7',
    remoteUri: '8',
    _endType: '9',
    _errorCause: '10',
    _id: '11',
    _isPlaying: false,
    _transferStatus: '12',
    _hasLocalVideo: false,
    _hasRemoteVideo: false,
    _sdpStatus: '13',
    _localMedia: '14',
    _remoteMedia: '15',
    _additionalInfo: '16',
    _shareScreen: false,
    _modifySdp: false,
    _audioCodecs: '17',
    _videoCodecs: '18',
    _tones: '19',
    remoteUser: '7',
    _callStatus: '20',
    _direction: '21',
    _logger: '22',
    _mediaSessionStatus: '23',
    _mediaDeviceStatus: '24',
    _outputMediaStream: '25',
    _notes: '26',
    _screenpops: '27',
    _inputMediaStream: '28',
    _request: '29',
    _peerConnection: '30',
  },
  {
    _events: '31',
    _eventsCount: 20,
    _id: '32',
    _ua: '33',
    _status: 2,
    _dialog: null,
    _earlyDialogs: '34',
    _contact: '35',
    _from_tag: '36',
    _to_tag: null,
    _connection: '30',
    _connectionPromiseQueue: '37',
    _request: '29',
    _is_canceled: false,
    _cancel_reason: '10',
    _is_confirmed: false,
    _late_sdp: false,
    _rtcOfferConstraints: '38',
    _rtcAnswerConstraints: null,
    _localMediaStream: '28',
    _localMediaStreamLocallyGenerated: false,
    _rtcReady: true,
    _iceReady: true,
    _timers: '39',
    _direction: '40',
    _local_identity: '41',
    _remote_identity: '42',
    _start_time: null,
    _end_time: null,
    _tones: null,
    _audioMuted: false,
    _videoMuted: false,
    _localHold: false,
    _remoteHold: false,
    _sessionTimers: '43',
    _referSubscribers: '44',
    _data: '45',
  },
  { extraHeaders: '46', sessionTimerExpires: 120 },
  { iceServers: '47' },
  { duration: 100, interToneGap: 500, channelType: '48' },
  {
    _isPlaying: false,
    _availableDevices: '49',
    _inStreamContexts: '50',
    _outStreamContexts: '51',
    _supportedDeviceTypes: '52',
    _outputVolume: 0.8,
    _inputVolume: 1,
    _ringVolume: 0.8,
    _eventEmitter: '6',
    _config: '53',
    _audioContext: '54',
  },
  { _events: '55', _eventsCount: 6 },
  '+16027059885',
  'sip:+16027059885@d698b0.sip.2600hz.com',
  'none',
  '',
  'e0927c4b-af26-4a4c-8a00-d2429acab47e',
  'transfer/NONE',
  'sdp/OFFER_PENDING',
  ['56', '57'],
  [],
  {},
  ['58', '59', '60', '61', '62'],
  ['63'],
  {},
  'callStatus/PROGRESS',
  'callDirection/OUTGOING',
  { memory: '64' },
  'mediaSessionStatus/IDLE',
  { audio: '65', video: '65' },
  {},
  { type: '66', data: '10' },
  [],
  {},
  {
    ua: '33',
    headers: '67',
    method: '68',
    ruri: '69',
    body: '70',
    extraHeaders: '71',
    to: '42',
    from: '41',
    call_id: '72',
    cseq: 8042,
    transaction: '73',
  },
  {},
  {},
  'unsd4frcqgfjm2h9smgq7udk2ejlt8',
  {
    _events: '74',
    _eventsCount: 7,
    _cache: '75',
    _configuration: '76',
    _dynConfiguration: '77',
    _dialogs: '78',
    _applicants: '79',
    _sessions: '80',
    _transport: '81',
    _contact: '82',
    _status: 1,
    _error: null,
    _transactions: '83',
    _data: '84',
    _closeTimer: null,
    _registrator: '85',
  },
  { unsd4frcqgfjm2h9smgq7udk2ejlt8yyj6yjarBSeFe: '86' },
  '<sip:user_d8c3fa82-f726-493b-b182-4d7@d698b0.sip.2600hz.com;gr=urn:uuid:56ef8179-bdf8-48a6-b56d-d2b00144fe86>',
  '7udk2ejlt8',
  {},
  { offerToReceiveAudio: true, offerToReceiveVideo: true, iceRestart: false },
  {
    ackTimer: null,
    expiresTimer: null,
    invite2xxTimer: null,
    userNoAnswerTimer: null,
  },
  'outgoing',
  { _uri: '87', _parameters: '88', _display_name: null },
  { _uri: '69', _parameters: '89', _display_name: null },
  {
    enabled: true,
    refreshMethod: '90',
    defaultExpires: 120,
    currentExpires: null,
    running: false,
    refresher: false,
    timer: null,
  },
  {},
  {},
  { invite: '91' },
  [],
  'RFC2833',
  ['92', '93', '94', '95'],
  ['96'],
  [],
  ['97', '98', '99'],
  { audio: '100', video: '101', screenShare: '102' },
  {},
  {
    'call.update': '103',
    'call.ended': '104',
    'media.device.update': '105',
    'audio.input.update': '106',
    'audio.output.update': '107',
    'video.input.update': '108',
  },
  { mode: '109', type: '110', payloads: '111' },
  { mode: '112', type: '113', payloads: '114' },
  'G722',
  'PCMA',
  'PCMU',
  'telephone-event',
  'CN',
  'H264',
  {},
  'mediaDeviceStatus/ACTIVE',
  'text',
  {
    Via: '115',
    'Max-Forwards': '116',
    To: '117',
    From: '118',
    'Call-ID': '119',
    CSeq: '120',
  },
  'INVITE',
  {
    _parameters: '121',
    _headers: '122',
    _scheme: '123',
    _user: '7',
    _host: '124',
  },
  'v=0\r\no=- 7887431409446478243 2 IN IP4 127.0.0.1\r\ns=-\r\nt=0 0\r\na=group:BUNDLE 0 1\r\na=extmap-allow-mixed\r\na=msid-semantic: WMS 396b205e-90f9-4951-9979-2357a0e74cdd\r\nm=audio 63321 UDP/TLS/RTP/SAVPF 111 63 103 104 9 0 8 106 105 13 110 112 113 126\r\nc=IN IP4 10.0.0.4\r\na=rtcp:9 IN IP4 0.0.0.0\r\na=candidate:1554775447 1 udp 2122262783 2601:642:c401:a2e0:284b:979e:5698:2652 60945 typ host generation 0 network-id 2 network-cost 10\r\na=candidate:2728715311 1 udp 2122194687 10.0.0.4 63321 typ host generation 0 network-id 1 network-cost 10\r\na=candidate:2717985027 1 tcp 1518283007 2601:642:c401:a2e0:284b:979e:5698:2652 9 typ host tcptype active generation 0 network-id 2 network-cost 10\r\na=candidate:1544433851 1 tcp 1518214911 10.0.0.4 9 typ host tcptype active generation 0 network-id 1 network-cost 10\r\na=ice-ufrag:YzZN\r\na=ice-pwd:CwjRtHkdY9cWynKFZhxxHE8y\r\na=ice-options:trickle\r\na=fingerprint:sha-256 11:95:A7:C3:30:96:37:18:5B:30:7E:9A:36:A9:D3:89:AF:FC:52:59:09:4A:BC:96:B3:1E:60:6A:D4:E6:12:98\r\na=setup:actpass\r\na=mid:0\r\na=extmap:1 urn:ietf:params:rtp-hdrext:ssrc-audio-level\r\na=extmap:2 http://www.webrtc.org/experiments/rtp-hdrext/abs-send-time\r\na=extmap:3 http://www.ietf.org/id/draft-holmer-rmcat-transport-wide-cc-extensions-01\r\na=extmap:4 urn:ietf:params:rtp-hdrext:sdes:mid\r\na=sendrecv\r\na=msid:396b205e-90f9-4951-9979-2357a0e74cdd d0ef7ad6-ecbc-4eb4-b150-ae591f0d781c\r\na=rtcp-mux\r\na=rtpmap:111 opus/48000/2\r\na=rtcp-fb:111 transport-cc\r\na=fmtp:111 minptime=10;useinbandfec=1\r\na=rtpmap:63 red/48000/2\r\na=fmtp:63 111/111\r\na=rtpmap:103 ISAC/16000\r\na=rtpmap:104 ISAC/32000\r\na=rtpmap:9 G722/8000\r\na=rtpmap:0 PCMU/8000\r\na=rtpmap:8 PCMA/8000\r\na=rtpmap:106 CN/32000\r\na=rtpmap:105 CN/16000\r\na=rtpmap:13 CN/8000\r\na=rtpmap:110 telephone-event/48000\r\na=rtpmap:112 telephone-event/32000\r\na=rtpmap:113 telephone-event/16000\r\na=rtpmap:126 telephone-event/8000\r\na=ssrc:125005440 cname:7Wh9XdQBIZLD1rfh\r\na=ssrc:125005440 msid:396b205e-90f9-4951-9979-2357a0e74cdd d0ef7ad6-ecbc-4eb4-b150-ae591f0d781c\r\nm=video 53540 UDP/TLS/RTP/SAVPF 96 97 98 99 100 101 35 36 37 38 102 122 127 121 125 107 108 109 124 120 39 40 41 42 43 44 45 46 47 48 123 119 114 115 116 49\r\nc=IN IP4 10.0.0.4\r\na=rtcp:9 IN IP4 0.0.0.0\r\na=candidate:1554775447 1 udp 2122262783 2601:642:c401:a2e0:284b:979e:5698:2652 55223 typ host generation 0 network-id 2 network-cost 10\r\na=candidate:2728715311 1 udp 2122194687 10.0.0.4 53540 typ host generation 0 network-id 1 network-cost 10\r\na=candidate:2717985027 1 tcp 1518283007 2601:642:c401:a2e0:284b:979e:5698:2652 9 typ host tcptype active generation 0 network-id 2 network-cost 10\r\na=candidate:1544433851 1 tcp 1518214911 10.0.0.4 9 typ host tcptype active generation 0 network-id 1 network-cost 10\r\na=ice-ufrag:YzZN\r\na=ice-pwd:CwjRtHkdY9cWynKFZhxxHE8y\r\na=ice-options:trickle\r\na=fingerprint:sha-256 11:95:A7:C3:30:96:37:18:5B:30:7E:9A:36:A9:D3:89:AF:FC:52:59:09:4A:BC:96:B3:1E:60:6A:D4:E6:12:98\r\na=setup:actpass\r\na=mid:1\r\na=extmap:14 urn:ietf:params:rtp-hdrext:toffset\r\na=extmap:2 http://www.webrtc.org/experiments/rtp-hdrext/abs-send-time\r\na=extmap:13 urn:3gpp:video-orientation\r\na=extmap:3 http://www.ietf.org/id/draft-holmer-rmcat-transport-wide-cc-extensions-01\r\na=extmap:5 http://www.webrtc.org/experiments/rtp-hdrext/playout-delay\r\na=extmap:6 http://www.webrtc.org/experiments/rtp-hdrext/video-content-type\r\na=extmap:7 http://www.webrtc.org/experiments/rtp-hdrext/video-timing\r\na=extmap:8 http://www.webrtc.org/experiments/rtp-hdrext/color-space\r\na=extmap:4 urn:ietf:params:rtp-hdrext:sdes:mid\r\na=extmap:10 urn:ietf:params:rtp-hdrext:sdes:rtp-stream-id\r\na=extmap:11 urn:ietf:params:rtp-hdrext:sdes:repaired-rtp-stream-id\r\na=recvonly\r\na=rtcp-mux\r\na=rtcp-rsize\r\na=rtpmap:96 VP8/90000\r\na=rtcp-fb:96 goog-remb\r\na=rtcp-fb:96 transport-cc\r\na=rtcp-fb:96 ccm fir\r\na=rtcp-fb:96 nack\r\na=rtcp-fb:96 nack pli\r\na=rtpmap:97 rtx/90000\r\na=fmtp:97 apt=96\r\na=rtpmap:98 VP9/90000\r\na=rtcp-fb:98 goog-remb\r\na=rtcp-fb:98 transport-cc\r\na=rtcp-fb:98 ccm fir\r\na=rtcp-fb:98 nack\r\na=rtcp-fb:98 nack pli\r\na=fmtp:98 profile-id=0\r\na=rtpmap:99 rtx/90000\r\na=fmtp:99 apt=98\r\na=rtpmap:100 VP9/90000\r\na=rtcp-fb:100 goog-remb\r\na=rtcp-fb:100 transport-cc\r\na=rtcp-fb:100 ccm fir\r\na=rtcp-fb:100 nack\r\na=rtcp-fb:100 nack pli\r\na=fmtp:100 profile-id=2\r\na=rtpmap:101 rtx/90000\r\na=fmtp:101 apt=100\r\na=rtpmap:35 VP9/90000\r\na=rtcp-fb:35 goog-remb\r\na=rtcp-fb:35 transport-cc\r\na=rtcp-fb:35 ccm fir\r\na=rtcp-fb:35 nack\r\na=rtcp-fb:35 nack pli\r\na=fmtp:35 profile-id=1\r\na=rtpmap:36 rtx/90000\r\na=fmtp:36 apt=35\r\na=rtpmap:37 VP9/90000\r\na=rtcp-fb:37 goog-remb\r\na=rtcp-fb:37 transport-cc\r\na=rtcp-fb:37 ccm fir\r\na=rtcp-fb:37 nack\r\na=rtcp-fb:37 nack pli\r\na=fmtp:37 profile-id=3\r\na=rtpmap:38 rtx/90000\r\na=fmtp:38 apt=37\r\na=rtpmap:102 H264/90000\r\na=rtcp-fb:102 goog-remb\r\na=rtcp-fb:102 transport-cc\r\na=rtcp-fb:102 ccm fir\r\na=rtcp-fb:102 nack\r\na=rtcp-fb:102 nack pli\r\na=fmtp:102 level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42001f\r\na=rtpmap:122 rtx/90000\r\na=fmtp:122 apt=102\r\na=rtpmap:127 H264/90000\r\na=rtcp-fb:127 goog-remb\r\na=rtcp-fb:127 transport-cc\r\na=rtcp-fb:127 ccm fir\r\na=rtcp-fb:127 nack\r\na=rtcp-fb:127 nack pli\r\na=fmtp:127 level-asymmetry-allowed=1;packetization-mode=0;profile-level-id=42001f\r\na=rtpmap:121 rtx/90000\r\na=fmtp:121 apt=127\r\na=rtpmap:125 H264/90000\r\na=rtcp-fb:125 goog-remb\r\na=rtcp-fb:125 transport-cc\r\na=rtcp-fb:125 ccm fir\r\na=rtcp-fb:125 nack\r\na=rtcp-fb:125 nack pli\r\na=fmtp:125 level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f\r\na=rtpmap:107 rtx/90000\r\na=fmtp:107 apt=125\r\na=rtpmap:108 H264/90000\r\na=rtcp-fb:108 goog-remb\r\na=rtcp-fb:108 transport-cc\r\na=rtcp-fb:108 ccm fir\r\na=rtcp-fb:108 nack\r\na=rtcp-fb:108 nack pli\r\na=fmtp:108 level-asymmetry-allowed=1;packetization-mode=0;profile-level-id=42e01f\r\na=rtpmap:109 rtx/90000\r\na=fmtp:109 apt=108\r\na=rtpmap:124 H264/90000\r\na=rtcp-fb:124 goog-remb\r\na=rtcp-fb:124 transport-cc\r\na=rtcp-fb:124 ccm fir\r\na=rtcp-fb:124 nack\r\na=rtcp-fb:124 nack pli\r\na=fmtp:124 level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=4d001f\r\na=rtpmap:120 rtx/90000\r\na=fmtp:120 apt=124\r\na=rtpmap:39 H264/90000\r\na=rtcp-fb:39 goog-remb\r\na=rtcp-fb:39 transport-cc\r\na=rtcp-fb:39 ccm fir\r\na=rtcp-fb:39 nack\r\na=rtcp-fb:39 nack pli\r\na=fmtp:39 level-asymmetry-allowed=1;packetization-mode=0;profile-level-id=4d001f\r\na=rtpmap:40 rtx/90000\r\na=fmtp:40 apt=39\r\na=rtpmap:41 H264/90000\r\na=rtcp-fb:41 goog-remb\r\na=rtcp-fb:41 transport-cc\r\na=rtcp-fb:41 ccm fir\r\na=rtcp-fb:41 nack\r\na=rtcp-fb:41 nack pli\r\na=fmtp:41 level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=f4001f\r\na=rtpmap:42 rtx/90000\r\na=fmtp:42 apt=41\r\na=rtpmap:43 H264/90000\r\na=rtcp-fb:43 goog-remb\r\na=rtcp-fb:43 transport-cc\r\na=rtcp-fb:43 ccm fir\r\na=rtcp-fb:43 nack\r\na=rtcp-fb:43 nack pli\r\na=fmtp:43 level-asymmetry-allowed=1;packetization-mode=0;profile-level-id=f4001f\r\na=rtpmap:44 rtx/90000\r\na=fmtp:44 apt=43\r\na=rtpmap:45 AV1/90000\r\na=rtcp-fb:45 goog-remb\r\na=rtcp-fb:45 transport-cc\r\na=rtcp-fb:45 ccm fir\r\na=rtcp-fb:45 nack\r\na=rtcp-fb:45 nack pli\r\na=rtpmap:46 rtx/90000\r\na=fmtp:46 apt=45\r\na=rtpmap:47 AV1/90000\r\na=rtcp-fb:47 goog-remb\r\na=rtcp-fb:47 transport-cc\r\na=rtcp-fb:47 ccm fir\r\na=rtcp-fb:47 nack\r\na=rtcp-fb:47 nack pli\r\na=fmtp:47 profile=1\r\na=rtpmap:48 rtx/90000\r\na=fmtp:48 apt=47\r\na=rtpmap:123 H264/90000\r\na=rtcp-fb:123 goog-remb\r\na=rtcp-fb:123 transport-cc\r\na=rtcp-fb:123 ccm fir\r\na=rtcp-fb:123 nack\r\na=rtcp-fb:123 nack pli\r\na=fmtp:123 level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=64001f\r\na=rtpmap:119 rtx/90000\r\na=fmtp:119 apt=123\r\na=rtpmap:114 red/90000\r\na=rtpmap:115 rtx/90000\r\na=fmtp:115 apt=114\r\na=rtpmap:116 ulpfec/90000\r\na=rtpmap:49 flexfec-03/90000\r\na=rtcp-fb:49 goog-remb\r\na=rtcp-fb:49 transport-cc\r\na=fmtp:49 repair-window=10000000\r\n',
  ['125', '126', '127', '128', '129'],
  'unsd4frcqgfjm2h9smgq',
  {
    _events: '130',
    _eventsCount: 0,
    type: '131',
    id: '132',
    ua: '33',
    transport: '81',
    request: '29',
    eventHandlers: '133',
    state: 2,
    B: 505,
  },
  {},
  { credentials: '134' },
  {
    authorization_user: '135',
    password: null,
    realm: '124',
    ha1: '136',
    authorization_jwt: null,
    display_name: null,
    uri: '87',
    contact_uri: '137',
    instance_id: '138',
    use_preloaded_route: false,
    session_timers: true,
    session_timers_refresh_method: '90',
    session_timers_force_refresher: false,
    no_answer_timeout: 60000,
    register: false,
    register_expires: 600,
    registrar_server: '139',
    connection_recovery_max_interval: 30,
    connection_recovery_min_interval: 2,
    via_host: '140',
    jssip_id: '141',
    hostport_params: '124',
  },
  { register: true },
  { unsd4frcqgfjm2h9smgq7udk2ejlt8yyj6yjarBSeFe: '86' },
  {},
  { unsd4frcqgfjm2h9smgq7udk2ejlt8: '1' },
  {
    status: 0,
    socket: '142',
    sockets: '143',
    recovery_options: '144',
    recover_attempts: 0,
    recovery_timer: null,
    close_requested: false,
    textDecoder: '145',
  },
  { pub_gruu: '146', temp_gruu: '147', uri: '137' },
  { nist: '148', nict: '149', ist: '150', ict: '151' },
  {},
  {
    _reg_id: 1,
    _ua: '33',
    _registrar: '139',
    _expires: 600,
    _call_id: '152',
    _cseq: 2,
    _to_uri: '87',
    _registrationTimer: 244,
    _registering: false,
    _registered: true,
    _contact: '153',
    _extraHeaders: '154',
    _extraContactParams: '10',
    _sipInstance: '155',
  },
  {
    _owner: '1',
    _ua: '33',
    _uac_pending_reply: false,
    _uas_pending_reply: false,
    _id: '156',
    _state: 1,
    _local_seqnum: 8042,
    _local_uri: '157',
    _remote_uri: '158',
    _remote_target: '159',
    _route_set: '160',
    _ack_seqnum: null,
  },
  {
    _parameters: '161',
    _headers: '162',
    _scheme: '123',
    _user: '135',
    _host: '124',
  },
  { tag: '36' },
  {},
  'UPDATE',
  ['125', '126'],
  { deviceId: '163', kind: '97', label: '164' },
  { deviceId: '165', kind: '99', label: '166' },
  { deviceId: '163', kind: '98', label: '167' },
  { deviceId: '168', kind: '98', label: '169' },
  {
    id: '11',
    hasVideo: false,
    hasScreenMedia: false,
    srcNode: '170',
    destNode: '171',
    gainNode: '172',
    rawStream: '173',
    amplStream: '28',
  },
  'audioinput',
  'audiooutput',
  'videoinput',
  { in: '174', out: '175' },
  { in: '176', out: '177', width: 1280, height: 720 },
  { cursor: '178', logicalSurface: false, screenAudio: false },
  { context: '6', once: false },
  { context: '6', once: false },
  { context: '6', once: false },
  { context: '6', once: false },
  { context: '6', once: false },
  { context: '6', once: false },
  'sendrecv',
  'audio',
  [
    '179',
    '180',
    '181',
    '182',
    '183',
    '184',
    '185',
    '186',
    '187',
    '188',
    '189',
    '190',
    '191',
    '192',
  ],
  'recvonly',
  'video',
  [
    '193',
    '194',
    '195',
    '196',
    '197',
    '198',
    '199',
    '200',
    '201',
    '202',
    '203',
    '204',
    '205',
    '206',
    '207',
    '208',
    '209',
    '210',
    '211',
    '212',
    '213',
    '214',
    '215',
    '216',
    '217',
    '218',
    '219',
    '220',
    '221',
    '222',
    '223',
    '224',
    '225',
    '226',
    '227',
    '228',
  ],
  ['229'],
  [69],
  ['230'],
  ['231'],
  ['72'],
  ['232'],
  {},
  {},
  'sip',
  'd698b0.sip.2600hz.com',
  'X-Foo: foo2',
  'X-Bar: bar2',
  'Contact: <sip:user_d8c3fa82-f726-493b-b182-4d7@d698b0.sip.2600hz.com;gr=urn:uuid:56ef8179-bdf8-48a6-b56d-d2b00144fe86>',
  'Content-Type: application/sdp',
  'Session-Expires: 120',
  {},
  'ict',
  'z9hG4bK1869333',
  {},
  {},
  'user_d8c3fa82-f726-493b-b182-4d7',
  'abab51dd71bd3dc4cfc7b445d062e30b',
  {
    _parameters: '233',
    _headers: '234',
    _scheme: '123',
    _user: '235',
    _host: '140',
    _port: null,
  },
  '56ef8179-bdf8-48a6-b56d-d2b00144fe86',
  {
    _parameters: '236',
    _headers: '237',
    _scheme: '123',
    _user: null,
    _host: '124',
  },
  'g3ups6jnts3g.invalid',
  'unsd4',
  { _url: '238', _sip_uri: '239', _via_transport: '240', _ws: '241' },
  ['242'],
  { max_interval: 30, min_interval: 2 },
  {},
  'sip:user_d8c3fa82-f726-493b-b182-4d7@d698b0.sip.2600hz.com;gr=urn:uuid:56ef8179-bdf8-48a6-b56d-d2b00144fe86',
  'sip:uloc-63e96715-676b-1-482ee42e@d698b0.sip.2600hz.com;gr',
  {},
  {},
  {},
  { z9hG4bK1869333: '73' },
  'mipas061p7018stt7nha00',
  '<sip:k760j2so@g3ups6jnts3g.invalid;transport=ws>;+sip.ice;reg-id=1;+sip.instance="<urn:uuid:56ef8179-bdf8-48a6-b56d-d2b00144fe86>"',
  [],
  '"<urn:uuid:56ef8179-bdf8-48a6-b56d-d2b00144fe86>"',
  { call_id: '72', local_tag: '36', remote_tag: '243' },
  {
    _parameters: '244',
    _headers: '245',
    _scheme: '123',
    _user: '135',
    _host: '124',
  },
  {
    _parameters: '246',
    _headers: '247',
    _scheme: '123',
    _user: '7',
    _host: '124',
  },
  {
    _parameters: '248',
    _headers: '249',
    _scheme: '123',
    _user: '7',
    _host: '250',
    _port: 11000,
  },
  ['251', '252'],
  {},
  {},
  'default',
  'MacBook Pro Microphone (Built-in)',
  'b33626909a06a9c4287d7526ce139e665bb89728fb2ff5d3e272f19cbb2be01d',
  'FaceTime HD Camera',
  'MacBook Pro Speakers (Built-in)',
  '6bbb3db5011c9de2a791c85a35533414b1b6e43adeee9436cca72cab38f8ef15',
  'E20 (HDMI)',
  {},
  {},
  {},
  {},
  { enabled: true, deviceIds: '253', element: null },
  { enabled: true, deviceIds: '254', element: '255' },
  { enabled: true, deviceIds: '256' },
  { enabled: false, deviceIds: '257' },
  'always',
  { payload: 111, codec: '258', rate: 48000, encoding: 2 },
  { payload: 63, codec: '259', rate: 48000, encoding: 2 },
  { payload: 103, codec: '260', rate: 16000 },
  { payload: 104, codec: '260', rate: 32000 },
  { payload: 9, codec: '58', rate: 8000 },
  { payload: 0, codec: '60', rate: 8000 },
  { payload: 8, codec: '59', rate: 8000 },
  { payload: 106, codec: '62', rate: 32000 },
  { payload: 105, codec: '62', rate: 16000 },
  { payload: 13, codec: '62', rate: 8000 },
  { payload: 110, codec: '61', rate: 48000 },
  { payload: 112, codec: '61', rate: 32000 },
  { payload: 113, codec: '61', rate: 16000 },
  { payload: 126, codec: '61', rate: 8000 },
  { payload: 96, codec: '261', rate: 90000 },
  { payload: 97, codec: '262', rate: 90000 },
  { payload: 98, codec: '263', rate: 90000 },
  { payload: 99, codec: '262', rate: 90000 },
  { payload: 100, codec: '263', rate: 90000 },
  { payload: 101, codec: '262', rate: 90000 },
  { payload: 35, codec: '263', rate: 90000 },
  { payload: 36, codec: '262', rate: 90000 },
  { payload: 37, codec: '263', rate: 90000 },
  { payload: 38, codec: '262', rate: 90000 },
  { payload: 102, codec: '63', rate: 90000 },
  { payload: 122, codec: '262', rate: 90000 },
  { payload: 127, codec: '63', rate: 90000 },
  { payload: 121, codec: '262', rate: 90000 },
  { payload: 125, codec: '63', rate: 90000 },
  { payload: 107, codec: '262', rate: 90000 },
  { payload: 108, codec: '63', rate: 90000 },
  { payload: 109, codec: '262', rate: 90000 },
  { payload: 124, codec: '63', rate: 90000 },
  { payload: 120, codec: '262', rate: 90000 },
  { payload: 39, codec: '63', rate: 90000 },
  { payload: 40, codec: '262', rate: 90000 },
  { payload: 41, codec: '63', rate: 90000 },
  { payload: 42, codec: '262', rate: 90000 },
  { payload: 43, codec: '63', rate: 90000 },
  { payload: 44, codec: '262', rate: 90000 },
  { payload: 45, codec: '264', rate: 90000 },
  { payload: 46, codec: '262', rate: 90000 },
  { payload: 47, codec: '264', rate: 90000 },
  { payload: 48, codec: '262', rate: 90000 },
  { payload: 123, codec: '63', rate: 90000 },
  { payload: 119, codec: '262', rate: 90000 },
  { payload: 114, codec: '259', rate: 90000 },
  { payload: 115, codec: '262', rate: 90000 },
  { payload: 116, codec: '265', rate: 90000 },
  { payload: 49, codec: '266', rate: 90000 },
  'SIP/2.0/WSS g3ups6jnts3g.invalid;branch=z9hG4bK1869333',
  '<sip:+16027059885@d698b0.sip.2600hz.com>',
  '<sip:user_d8c3fa82-f726-493b-b182-4d7@d698b0.sip.2600hz.com>;tag=7udk2ejlt8',
  '8042 INVITE',
  { transport: '267' },
  {},
  'k760j2so',
  {},
  {},
  'wss://rtc.calling.io:5065',
  'sip:rtc.calling.io:5065;transport=ws',
  'WSS',
  {},
  { socket: '142', weight: 0, status: 0 },
  'yyj6yjarBSeFe',
  {},
  {},
  {},
  {},
  { transport: '268', alias: '269' },
  {},
  '162.216.19.89',
  '<sip:162.216.19.89:5065;transport=ws;r2=on;lr=on;ftag=7udk2ejlt8>',
  '<sip:162.216.19.89;r2=on;lr=on;ftag=7udk2ejlt8>',
  [],
  ['163'],
  {},
  ['165'],
  [],
  'opus',
  'red',
  'ISAC',
  'VP8',
  'rtx',
  'VP9',
  'AV1',
  'ulpfec',
  'flexfec-03',
  'ws',
  'udp',
  '162.216.19.89~11000~1',
]);
